import React, { useContext, useCallback, memo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import Drawer from '@mui/material/Drawer';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import { alpha } from '@mui/material';
import BusinessIcon from '@mui/icons-material/Business';
import PlaceIcon from '@mui/icons-material/Place';
import PeopleIcon from '@mui/icons-material/People';
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined';
import LogoutOutlinedIcon from '@mui/icons-material/LogoutOutlined';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import DashboardOutlinedIcon from '@mui/icons-material/DashboardOutlined';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import ExtensionIcon from '@mui/icons-material/Extension';
import SecurityIcon from '@mui/icons-material/Security';
import KeyIcon from '@mui/icons-material/Key';
import { palette, typography, theme } from '../theme/colors';
import { ColorModeContext } from '../contexts/ColorModeContext';
import { useAnimations } from '../contexts/AnimationsContext';

const DRAWER_WIDTH = 220;

interface MenuItem {
  label: string;
  path: string;
  icon: React.ReactNode;
}

interface MenuSection {
  title: string;
  items: MenuItem[];
}

const menuSections: MenuSection[] = [
  {
    title: 'MAIN MENU',
    items: [
      { label: 'Organizations', path: '/organizations', icon: <BusinessIcon /> },
      { label: 'Locations', path: '/locations', icon: <PlaceIcon /> },
      { label: 'Users', path: '/users', icon: <PeopleIcon /> },
      { label: 'Roles', path: '/roles', icon: <SecurityIcon /> },
    ],
  },
  {
    title: 'SUBSCRIPTIONS',
    items: [
      { label: 'Dashboard', path: '/subscriptions', icon: <DashboardOutlinedIcon /> },
      { label: 'Plans', path: '/subscriptions/plans', icon: <CreditCardIcon /> },
      { label: 'Modules', path: '/subscriptions/modules', icon: <ExtensionIcon /> },
    ],
  },
  {
    title: 'DESKTOP',
    items: [
      { label: 'Licenses', path: '/licenses', icon: <KeyIcon /> },
    ],
  },
];

interface SidebarColors {
  bg: string;
  hover: string;
  active: string;
  border: string;
  text: string;
  textActive: string;
  textPrimary: string;
  textMuted: string;
}

interface NavItemProps {
  item: MenuItem;
  itemIndex: number;
  isActive: boolean;
  onNavigate: (path: string) => void;
  animationsEnabled: boolean;
  colors: SidebarColors;
}

const NavItem = memo(({ item, itemIndex, isActive, onNavigate, animationsEnabled, colors }: NavItemProps) => {
  const handleClick = useCallback(() => {
    onNavigate(item.path);
  }, [item.path, onNavigate]);

  return (
    <ListItem
      disablePadding
      sx={{
        mb: 0.25,
        ...(animationsEnabled ? {
          opacity: 0,
          animation: 'itemFadeIn 0.2s ease-out forwards',
          animationDelay: `${itemIndex * 0.02}s`,
          '@keyframes itemFadeIn': {
            from: { opacity: 0 },
            to: { opacity: 1 },
          },
        } : {}),
      }}
    >
      <ListItemButton
        selected={isActive}
        onClick={handleClick}
        sx={{
          py: 1,
          px: 1.5,
          mx: 1,
          borderRadius: '6px',
          transition: `all ${theme.transitions.fast}`,
          backgroundColor: isActive ? alpha(palette.primary, 0.08) : 'transparent',
          '&.Mui-selected': {
            backgroundColor: alpha(palette.primary, 0.08),
          },
          '&:hover': {
            backgroundColor: alpha(colors.text, 0.04),
            '& .nav-item-icon': {
              color: palette.primary,
            },
            '& .nav-item-label .MuiTypography-root': {
              color: colors.textPrimary,
            },
          },
        }}
      >
        <ListItemIcon
          className="nav-item-icon"
          sx={{
            minWidth: 32,
            color: isActive ? palette.primary : colors.text,
            transition: `all ${theme.transitions.fast}`,
            '& svg': {
              fontSize: 18,
            },
          }}
        >
          {item.icon}
        </ListItemIcon>
        <ListItemText
          className="nav-item-label"
          primary={item.label}
          primaryTypographyProps={{
            fontSize: '13px',
            fontWeight: isActive ? typography.fontWeight.semibold : typography.fontWeight.medium,
            fontFamily: typography.fontFamily.body,
          }}
          sx={{
            '& .MuiTypography-root': {
              color: isActive ? colors.textPrimary : colors.text,
              transition: `all ${theme.transitions.fast}`,
            },
          }}
        />
      </ListItemButton>
    </ListItem>
  );
});

NavItem.displayName = 'NavItem';

interface SidebarProps {
  mobileOpen: boolean;
  onMobileClose: () => void;
}

export const Sidebar = ({ mobileOpen, onMobileClose }: SidebarProps) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { mode } = useContext(ColorModeContext);
  const { animationsEnabled } = useAnimations();

  const colors: SidebarColors = mode === 'dark' ? {
    bg: palette.sidebarBg,
    hover: palette.sidebarHover,
    active: palette.sidebarActive,
    border: palette.sidebarBorder,
    text: palette.sidebarText,
    textActive: palette.sidebarTextActive,
    textPrimary: palette.textPrimary,
    textMuted: palette.textMuted,
  } : {
    bg: palette.light.sidebarBg,
    hover: palette.light.sidebarHover,
    active: palette.light.sidebarActive,
    border: palette.light.sidebarBorder,
    text: palette.light.sidebarText,
    textActive: palette.light.sidebarTextActive,
    textPrimary: palette.light.textPrimary,
    textMuted: palette.light.textMuted,
  };

  const isActive = useCallback((path: string) => {
    if (path === '/') {
      return location.pathname === '/';
    }
    return location.pathname.startsWith(path);
  }, [location.pathname]);

  const handleNavigate = useCallback((path: string) => {
    navigate(path);
    onMobileClose();
  }, [navigate, onMobileClose]);

  const handleLogout = useCallback(() => {
    localStorage.removeItem('token');
    navigate('/login');
  }, [navigate]);

  const drawerContent = (
    <Box sx={{
      display: 'flex',
      flexDirection: 'column',
      height: '100%',
      backgroundColor: colors.bg,
    }}>
      {/* Logo/Brand Section */}
      <Box
        sx={{
          py: 2,
          px: 2.5,
          borderBottom: `1px solid ${colors.border}`,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box
            sx={{
              width: 32,
              height: 32,
              borderRadius: '8px',
              background: `linear-gradient(135deg, ${palette.primary} 0%, ${palette.accent} 100%)`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <AdminPanelSettingsIcon sx={{ color: '#fff', fontSize: 18 }} />
          </Box>
          <Box>
            <Typography
              sx={{
                fontFamily: typography.fontFamily.display,
                fontWeight: typography.fontWeight.bold,
                color: colors.textPrimary,
                fontSize: '14px',
                lineHeight: 1,
              }}
            >
              NEXERA
            </Typography>
            <Typography
              sx={{
                fontFamily: typography.fontFamily.mono,
                color: colors.textMuted,
                fontSize: '10px',
                textTransform: 'uppercase',
                letterSpacing: '0.1em',
              }}
            >
              Console
            </Typography>
          </Box>
        </Box>
      </Box>

      {/* Menu Sections */}
      <Box sx={{ flexGrow: 1, overflowY: 'auto', py: 2 }}>
        {menuSections.map((section, sectionIndex) => (
          <Box key={section.title} sx={{ mb: 2 }}>
            <Typography
              sx={{
                px: 2.5,
                mb: 0.75,
                fontFamily: typography.fontFamily.mono,
                fontWeight: typography.fontWeight.medium,
                color: colors.textMuted,
                fontSize: '10px',
                textTransform: 'uppercase',
                letterSpacing: '0.1em',
              }}
            >
              {section.title}
            </Typography>
            <List disablePadding>
              {section.items.map((item, itemIndex) => (
                <NavItem
                  key={item.path}
                  item={item}
                  itemIndex={sectionIndex * 10 + itemIndex}
                  isActive={isActive(item.path)}
                  onNavigate={handleNavigate}
                  animationsEnabled={animationsEnabled}
                  colors={colors}
                />
              ))}
            </List>
          </Box>
        ))}

        {/* System Section */}
        <Box sx={{ mt: 2, pt: 2, borderTop: `1px solid ${colors.border}` }}>
          <Typography
            sx={{
              px: 2.5,
              mb: 0.75,
              fontFamily: typography.fontFamily.mono,
              fontWeight: typography.fontWeight.medium,
              color: colors.textMuted,
              fontSize: '10px',
              textTransform: 'uppercase',
              letterSpacing: '0.1em',
            }}
          >
            SYSTEM
          </Typography>
          <List disablePadding>
            <ListItem disablePadding sx={{ mb: 0.25 }}>
              <ListItemButton
                onClick={() => handleNavigate('/settings')}
                sx={{
                  py: 1,
                  px: 1.5,
                  mx: 1,
                  borderRadius: '6px',
                  transition: `all ${theme.transitions.fast}`,
                  '&:hover': {
                    backgroundColor: alpha(colors.text, 0.04),
                  },
                }}
              >
                <ListItemIcon sx={{ minWidth: 32, color: colors.text, '& svg': { fontSize: 18 } }}>
                  <SettingsOutlinedIcon />
                </ListItemIcon>
                <ListItemText
                  primary="Settings"
                  primaryTypographyProps={{
                    fontSize: '13px',
                    fontWeight: typography.fontWeight.medium,
                    fontFamily: typography.fontFamily.body,
                    color: colors.text,
                  }}
                />
              </ListItemButton>
            </ListItem>
            <ListItem disablePadding>
              <ListItemButton
                onClick={handleLogout}
                sx={{
                  py: 1,
                  px: 1.5,
                  mx: 1,
                  borderRadius: '6px',
                  transition: `all ${theme.transitions.fast}`,
                  '&:hover': {
                    backgroundColor: alpha(palette.error, 0.08),
                    '& .MuiListItemIcon-root': {
                      color: palette.error,
                    },
                    '& .MuiTypography-root': {
                      color: palette.error,
                    },
                  },
                }}
              >
                <ListItemIcon sx={{ minWidth: 32, color: colors.text, '& svg': { fontSize: 18 } }}>
                  <LogoutOutlinedIcon />
                </ListItemIcon>
                <ListItemText
                  primary="Logout"
                  primaryTypographyProps={{
                    fontSize: '13px',
                    fontWeight: typography.fontWeight.medium,
                    fontFamily: typography.fontFamily.body,
                    color: colors.text,
                  }}
                />
              </ListItemButton>
            </ListItem>
          </List>
        </Box>
      </Box>
    </Box>
  );

  return (
    <>
      {/* Mobile drawer */}
      <Drawer
        variant="temporary"
        open={mobileOpen}
        onClose={onMobileClose}
        ModalProps={{ keepMounted: true }}
        sx={{
          display: { xs: 'block', sm: 'none' },
          '& .MuiDrawer-paper': {
            boxSizing: 'border-box',
            width: DRAWER_WIDTH,
            backgroundColor: colors.bg,
            borderRight: 'none',
          },
        }}
      >
        {drawerContent}
      </Drawer>

      {/* Desktop drawer */}
      <Drawer
        variant="permanent"
        sx={{
          display: { xs: 'none', sm: 'block' },
          width: DRAWER_WIDTH,
          flexShrink: 0,
          '& .MuiDrawer-paper': {
            width: DRAWER_WIDTH,
            boxSizing: 'border-box',
            backgroundColor: colors.bg,
            borderRight: 'none',
          },
        }}
      >
        {drawerContent}
      </Drawer>
    </>
  );
};
